/**
 * Mirrors packages/amghighlow_v2/lib/data/models/bet_result_model.dart `parseResult()`.
 *
 * Format: "<firstCard>,<secondCard>,<winningBetItem>"
 *   e.g. "12,7,H301" or "5,12,L302"
 *
 * `winningBetItem` has the leading character stripped before parsing as int
 * (matches `parts[2].substring(1)` in the Flutter source).
 */
import type { GameResult } from '../types/game';
import { betTypeFromItemId } from '../types/game';

export interface ParsedResult {
  firstCard: number; // raw 1..52
  secondCard: number; // raw 1..52
  winningBetItemId: number;
  gameResult: GameResult;
}

export function parseResultString(raw: string): ParsedResult {
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (parts.length < 3) {
    throw new Error(`Invalid HighLow result format: ${raw}`);
  }

  const firstCard = parseIntStrict(parts[0]);
  const secondCard = parseIntStrict(parts[1]);

  // Strip leading prefix char (H/L/etc) then parse remaining digits.
  const rawWin = parts[2];
  const winStr = rawWin.length > 1 ? rawWin.substring(1) : rawWin;
  const winningBetItemId = parseIntStrict(winStr);

  return {
    firstCard,
    secondCard,
    winningBetItemId,
    gameResult: resolveResult(firstCard, secondCard, winningBetItemId),
  };
}

/**
 * Parse the GameInitialInfo CSV for the next-round info.
 * e.g. "5,62.22%,33.33%,0.7,3,Old" — first element is the next start card.
 * Returns null if not parseable.
 */
export function parseNextFirstCard(gameInitialInfo: string): number | null {
  if (!gameInitialInfo) return null;
  const first = gameInitialInfo.split(',')[0]?.trim() ?? '';
  if (!first) return null;
  const n = parseInt(first, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseIntStrict(raw: string): number {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) throw new Error(`Invalid integer: ${raw}`);
  return n;
}

function resolveResult(
  firstCard: number,
  secondCard: number,
  winningBetItemId: number,
): GameResult {
  const bt = betTypeFromItemId(winningBetItemId);
  if (bt === 'high') return 'high';
  if (bt === 'low') return 'low';
  // Fallback: derive from card ranks (1..13 modulo).
  const aRank = ((firstCard - 1) % 13) + 1;
  const bRank = ((secondCard - 1) % 13) + 1;
  if (bRank > aRank) return 'high';
  if (bRank < aRank) return 'low';
  return 'tie';
}
